import type { Editor } from "@tiptap/core";
import { useEditorState } from "@tiptap/react";
import {
  BetweenHorizontalEnd,
  BetweenHorizontalStart,
  BetweenVerticalEnd,
  BetweenVerticalStart,
  ColumnsSettingsIcon,
  PanelLeft,
  PanelTop,
  Square,
  TableCellsMerge,
  TableCellsSplit,
  Trash2,
} from "lucide-react";

import { Button } from "simplydo/react/ui/button";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Icon, type IconData } from "simplydo/react/ui/icon";

import { useEditorContext } from "../../editor/EditorContext";
import { useFloatingToolbar } from "./context";
import { shouldShowTableSection } from "./predicates";

type TableCommand =
  | "addColumnBefore"
  | "addColumnAfter"
  | "deleteColumn"
  | "addRowBefore"
  | "addRowAfter"
  | "deleteRow"
  | "mergeCells"
  | "splitCell"
  | "toggleHeaderColumn"
  | "toggleHeaderRow"
  | "toggleHeaderCell"
  | "mergeOrSplit"
  | "deleteTable";

const TABLE_ACTIONS: Array<
  { label: string; icon: IconData; command: TableCommand } | { divider: true }
> = [
  {
    label: "Add column before",
    icon: BetweenVerticalStart,
    command: "addColumnBefore",
  },
  {
    label: "Add column after",
    icon: BetweenVerticalEnd,
    command: "addColumnAfter",
  },
  { label: "Delete column", icon: Trash2, command: "deleteColumn" },
  { divider: true },
  {
    label: "Add row before",
    icon: BetweenHorizontalStart,
    command: "addRowBefore",
  },
  {
    label: "Add row after",
    icon: BetweenHorizontalEnd,
    command: "addRowAfter",
  },
  { label: "Delete row", icon: Trash2, command: "deleteRow" },
  { divider: true },
  { label: "Merge cells", icon: TableCellsMerge, command: "mergeCells" },
  { label: "Split cell", icon: TableCellsSplit, command: "splitCell" },
  { divider: true },
  { label: "Toggle header row", icon: PanelTop, command: "toggleHeaderRow" },
  {
    label: "Toggle header column",
    icon: PanelLeft,
    command: "toggleHeaderColumn",
  },
  { label: "Toggle header cell", icon: Square, command: "toggleHeaderCell" },
  { divider: true },
  { label: "Delete table", icon: Trash2, command: "deleteTable" },
];

const canRunTableCommand = (editor: Editor, command: TableCommand) => {
  const can = editor.can() as Partial<Record<TableCommand, () => boolean>>;
  return can[command]?.() ?? false;
};

/**
 * Table options dropdown for the unified floating toolbar.
 * Hidden while link/file edit mode is active so expanded UIs stay exclusive.
 */
export const TableSection = () => {
  const { editor, commands } = useEditorContext();
  const { features, mode, openTableMenu, closeTableMenu } =
    useFloatingToolbar();

  const tableState = useEditorState({
    editor,
    selector: ({ editor: instance }) => {
      if (!instance || instance.isDestroyed) {
        return {
          canShow: false,
          enabled: {} as Partial<Record<TableCommand, boolean>>,
        };
      }
      return {
        canShow: shouldShowTableSection(instance, { requireFocus: false }),
        enabled: Object.fromEntries(
          TABLE_ACTIONS.flatMap((action) =>
            "divider" in action
              ? []
              : [
                  [
                    action.command,
                    canRunTableCommand(instance, action.command),
                  ],
                ],
          ),
        ) as Partial<Record<TableCommand, boolean>>,
      };
    },
  });

  const canShow = tableState?.canShow ?? false;
  const enabled = tableState?.enabled ?? {};

  if (!features.table || !editor || !canShow) {
    return null;
  }

  // Expanded link/file edit replaces the dropdown entirely.
  if (mode.type === "editLink" || mode.type === "editFile") {
    return null;
  }

  return (
    <Dropdown
      trigger={
        <Button
          type="button"
          aria-label="Table options"
          variant="outline"
          size="small"
          onMouseDown={(event) => event.preventDefault()}
        >
          <Icon icon={ColumnsSettingsIcon} />
        </Button>
      }
      onOpenChange={(open) => {
        if (open) {
          openTableMenu();
        } else {
          closeTableMenu();
        }
      }}
      options={TABLE_ACTIONS.map((action, index) =>
        "divider" in action
          ? { key: `divider-${index}`, divider: true as const }
          : {
              key: action.label,
              value: action.label,
              text: action.label,
              icon: action.icon,
              disabled: !enabled[action.command],
              onClick: (event) => {
                event.preventDefault();
                commands[action.command]?.();
              },
            },
      )}
    />
  );
};
